import { useCallback, useEffect, useMemo } from "react";
import { DEFAULT_UI_LANGUAGE, type UiLanguage } from "@t3tools/contracts/settings";

import { useClientSettings } from "~/hooks/useSettings";
import {
  applyDocumentLanguage,
  localeTag,
  resolveUiLanguage,
  type ResolvedUiLanguage,
} from "./locale";
import { translate, type MessageVars, type Translator } from "./translate";
import type { MessageKey } from "./messages";

export function useI18n(): {
  readonly language: ResolvedUiLanguage;
  readonly locale: string;
  readonly preference: UiLanguage;
  readonly t: Translator;
} {
  const preference = useClientSettings((settings) => settings.uiLanguage ?? DEFAULT_UI_LANGUAGE);
  const language = useMemo(() => resolveUiLanguage(preference), [preference]);
  const locale = localeTag(language);

  useEffect(() => {
    applyDocumentLanguage(language);
  }, [language]);

  const t = useCallback<Translator>(
    (key: MessageKey, vars?: MessageVars) => translate(language, key, vars),
    [language],
  );

  return { language, locale, preference, t };
}
